<template lang="pug">
  y-dialog(v-model="showDialog" :fullscreen="$vuetify.breakpoint.xsOnly" max-width="600" persistent scrollable content-class="round-3xl")
    v-card(min-height="600")
      v-card-title.pa-4
        v-row(no-gutters align="center")
          v-col(cols="10" offset="1")
            div.text-xl.font-bold.neutral700--text.text-center {{ $t('review_item_details', { item:titleText }) }}
          v-col(cols="1" align="right")
            v-btn.ml-n3(text icon @click="closeResetDialog")
              v-icon(color="neutral700" :size="24") mdi-close
      v-divider
      v-card-text(:class="$vuetify.breakpoint.xsOnly ? 'pa-4' : 'pa-8'")
        div.d-flex.justify-center.mt-16(v-if="isFetching")
          v-progress-circular(:width="2" :size="50" color="primary" indeterminate)
        template(v-else-if="isError")
          .d-flex.flex-column.align-center.justify-center.fill-height
            v-icon(size="60" color="danger500") mdi-alert-circle
            div.text-lg.danger500--text.mt-4 {{ $t('error') }}
        //- For clinic
        template(v-else-if="appointment")
          div.d-flex.align-center
            div.text-sm.font-semibold.neutral500--text {{ $t('patient_detail') }}
            v-spacer
            v-btn.button-xs.neutral700--text.px-0(text @click="openEditPatientDetailsDialog = true") {{ $t('edit') }}
          v-card.pa-4.round-lg.mt-1(outlined)
            v-row(no-gutters)
              v-col(cols="6")
                div.text-xs.neutral500--text {{ $t('name') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.patient.name || '-' }}
              v-col(cols="6")
                div.text-xs.neutral500--text {{ $t('nric_passport') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.patient.ic || '-' }}
              v-col.pt-4(cols="6")
                div.text-xs.neutral500--text {{ $t('email') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.patient.email || '-' }}
              v-col.pt-4(cols="6")
                div.text-xs.neutral500--text {{ $t('phone_number') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.patient.phone ?  `+${appointment.patient.phone}` : '-' }}
              v-col.pt-4(cols="12")
                div.text-xs.neutral500--text {{ $t('address') }}
                div.text-sm.neutral700--text.mt-1 {{ checkEmptyAddress(appointment.patient.address) ? '-' : getAddressInString(appointment.patient.address) }}

          div.d-flex.align-center.mt-6
            div.text-sm.font-semibold.neutral500--text {{ $t('appointment_detail') }}
            v-spacer
            v-btn.button-xs.neutral700--text.px-0(text @click="openEditAppointmentDetailsDialog = true") {{ $t('edit') }}
          v-card.round-lg.mt-1(outlined)
            v-row.pa-4(no-gutters)
              v-col(cols="6")
                div.text-xs.neutral500--text {{ $t('doctor') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.provider.name || '-' }}
              v-col(cols="6")
                div.text-xs.neutral500--text {{ $t('service') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.service.name || '-' }}
              v-col.pt-4(cols="6")
                div.text-xs.neutral500--text {{ $t('date') }}
                div.text-sm.neutral700--text.mt-1 {{ $options.filters.humanizeDate(appointment.date) || '-' }}
              v-col.pt-4(cols="6")
                div.text-xs.neutral500--text {{ $t('time') }}
                div.text-sm.neutral700--text.mt-1 {{ $options.filters.displayTime2(appointment.time) || '-' }}
              v-col.pt-4(cols="6")
                div.text-xs.neutral500--text {{ $t('booking_fees') }}
                div.text-sm.neutral700--text.mt-1 {{ (appointment.booking_fee && appointment.booking_fee > 0) ? this.$options.filters.displayPrice(appointment.booking_fee, $currency) : $t('no_fees_required') }}
              v-col.pt-4(cols="6")
                div.text-xs.neutral500--text {{ $t('booking_name') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.lead.name || '-' }}
              v-col.pt-4(cols="6")
                div.text-xs.neutral500--text {{ $t('booking_phone_number') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.lead.phone ?  `+${appointment.lead.phone}` : '-' }}
              v-col.pt-4(cols="6")
                div.text-xs.neutral500--text {{ $t('booking_email') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.lead.email || '-' }}
              v-col.pt-4(cols="12")
                div.text-xs.neutral500--text {{ $t('booking_notes') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.lead.remarks || '-' }}
            template(v-if="appointment.lead.extra_fields_data && appointment.lead.extra_fields_data.length > 0")
              v-divider
              v-row.px-4.pb-4(no-gutters)
                template(v-for="(field, fidx) in appointment.lead.extra_fields_data")
                  v-col.pt-4(v-if="field.enabled" cols="6" :key="`field-${fidx}`")
                    div.text-xs.neutral500--text {{ field.field_name }}
                    div.text-sm.neutral700--text.mt-1 {{ field.data || '-' }}
              v-col.pt-4(v-if="tabIndex === 3 && appointment.lead.order_remarks && appointment.lead.order_remarks.cancelled_reason" cols="6")
                div.text-xs.neutral500--text {{ $t('reason_for_cancellation') }}
                div.text-sm.neutral700--text.mt-1 {{ appointment.lead.order_remarks.cancelled_reason }}

          template(v-if="appointment.preliminary_questions && appointment.preliminary_questions.length > 0")
            div.d-flex.align-center.mt-6
              div.text-sm.font-semibold.neutral500--text {{ $t('preliminary_questions') }}
            v-card.px-4.pb-4.round-lg.mt-3(outlined)
              v-row(no-gutters)
                template(v-for="(question, qidx) in appointment.preliminary_questions")
                  v-col.pt-4(v-if="question.enabled" cols="12" :key="`field-${qidx}`")
                    div.text-xs.neutral500--text {{ question.name }}
                    div.text-sm.neutral700--text.mt-1 {{ question.data || '-' }}
        //- For review or clinic without appointment
        template(v-else-if="selectedCustomer")
          div.d-flex.align-center
            div.text-sm.font-semibold.neutral500--text {{ os === 'clinic' ? $t('patient_detail') : $t('customer_detail') }}
            v-spacer
            v-btn.button-xs.neutral700--text.px-0(v-if="selectedAppointment" text @click="openEditCustomerDetailDialog = true") {{ $t('edit') }}
          v-card.pa-4.round-lg.mt-1(outlined)
            v-row(no-gutters)
              v-col(cols="6")
                div.text-xs.neutral500--text {{ $t('name') }}
                div.text-sm.neutral700--text.mt-1 {{ selectedCustomer.name || '-' }}
              v-col(cols="6")
                div.text-xs.neutral500--text {{ $t('phone_number') }}
                div.text-sm.neutral700--text.mt-1 {{ selectedCustomer.phone ?  selectedCustomer.phone : '-' }}
              v-col.pt-4(cols="12")
                div.text-xs.neutral500--text {{ $t('email') }}
                div.text-sm.neutral700--text.mt-1 {{ selectedCustomer.email || '-' }}
              v-col.pt-4(cols="12")
                div.text-xs.neutral500--text {{ $t('address') }}
                div.text-sm.neutral700--text.mt-1 {{ checkEmptyAddress(selectedCustomer.address) ? '-' : getAddressInString(selectedCustomer.address) }}
      v-divider
      v-card-actions.py-4(v-if="appointment || selectedCustomer" :class="$vuetify.breakpoint.xsOnly ? 'px-4' : 'px-8'")
        v-btn.button-md.neutral700--text.px-0.underline(text @click="closeResetDialog") {{ $t('dismiss') }}
        v-spacer
        v-btn.button-md(:loading="isLoading" :disabled="os === 'clinic' && appointment ? !appointment.patient.phone : !selectedCustomer.phone" color="neutral700" depressed outlined @click="clickWhatsapp")
          v-icon.ml-n1(left) mdi-whatsapp
          | WhatsApp
      .dialogs
        edit-appointment-details(v-if="openEditAppointmentDetailsDialog" :open.sync="openEditAppointmentDetailsDialog" :selectedAppointment="selectedAppointment" @reset="resetData")
        edit-patient-details(v-if="openEditPatientDetailsDialog" :open.sync="openEditPatientDetailsDialog" :selectedAppointment="selectedAppointment" @reset="resetData")
        edit-customer-details-dialog(v-if="openEditCustomerDetailDialog" :open.sync="openEditCustomerDetailDialog" :selectedCustomer="selectedCustomer" @reset="$emit('reset', $event)")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import EditCustomerDetailsDialog from '@/components/review/dialogs/EditCustomerDetailsDialog.vue'
// Clinic
import EditAppointmentDetails from '@/components/clinic/dialogs/EditAppointmentDetails.vue'
import EditPatientDetails from '@/components/clinic/dialogs/EditPatientDetails.vue'

export default {
  components: { 
    EditCustomerDetailsDialog,
    EditAppointmentDetails,
    EditPatientDetails
  },
  props: {
    open: {
      type: Boolean,
    },
    selectedCustomer: {
      type: Object,
      default: null
    },
    selectedAppointment: {
      type: Object,
      default: null
    },
    tabIndex: {
      type: Number,
      default: null
    },
    os: {
      type: String,
      default: 'review',
      validator(value) {
        return ['clinic', 'donation', 'connect', 'review'].includes(value)
      }
    },
  },
  data () {
    return {
      isFetching: false,
      isError: false,
      isLoading: false,
      customer: null,
      appointment: null,
      openEditAppointmentDetailsDialog: false,
      openEditPatientDetailsDialog: false,
      openEditCustomerDetailDialog: false,
      openMarkAsArrivedDialog: false,
      openRescheduleDateDialog: false,
      openCancelAppointmentDialog: false,
      openFollowUpDialog: false,
      openRecordPaymentDialog: false,
      openMarkSentPendingDialog: false,
      hasChanges: false,
      statuses: [
        { status: 'new,processed', id: 0 },
        { status: 'received', id: 1 },
        { status: 'absent', id: 2 },
        { status: 'cancelled', id: 3 },
      ],
      actions: [
        { id: 'mark', text: this.$t('mark_as_arrived'), action: () => (this.openMarkAsArrivedDialog = true) },
        { id: 'reschedule', text: this.$t('reschedule_new_date'), action: () => (this.openRescheduleDateDialog = true) },
        { id: 'cancel', text: this.$t('cancel_this_appointment'), action: () => (this.openCancelAppointmentDialog = true) },
        { id: 'followup', text: this.$t('create_followup_appointment'), action: () => (this.openFollowUpDialog = true) },
        { id: 'record', text: this.$t('record_payment'), action: () => (this.openRecordPaymentDialog = true) },
      ],
      rules: {
        not_empty: [val => (val || '').length > 0 || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 1|| `Amount should be more than ${this.$currency.symbol} 1`],
        not_empty_select: [v => !!v || 'This field is required']
      },
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      clinic: 'clinic/getClinic'
    }),
    showDialog: {
      get () {
        return this.open
      },
      set (val) {
        this.$emit('update:open', val)
      }
    },
    messageTemplate() {
      return this.reviewSetting.sms_template
    },
    appointmentLink() {
      return `https://${this.company.slug}.yezza.co/appointment`
    },
    reviewLink() {
      if (process.env.VUE_APP_ENV === 'prod') {
        return `https://review.yezza.co/review`
      }
      return `https://review-test.yezza.co/review`
    },
    titleText() {
      switch (this.os) {
        case 'clinic':
          return this.$t('appointment')

        case 'review':
          return this.$t('customer')
      
        default:
          return ''
      }
    }
  },
  async mounted () {
    try {
      this.isFetching = true
      let allFetching = [this.fetchReviewSetting()]
      if (this.os === 'clinic' && this.selectedAppointment) allFetching.push(this.fetchAppointment())
      await Promise.all([...allFetching])
    } catch (error) {
      this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      this.isError = true
    } finally {
      this.isFetching = false
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    closeResetDialog() {
      this.$emit('closed')
      this.showDialog = false
    },
    resetData() {
      // For clinic
      this.hasChanges = true
      this.fetchAppointment()
    },
    checkEmptyAddress (address) {
      if (!address) {
        return true
      }
      if (Object.keys(address).length === 0) {
        return true
      } else if (Object.values(address).every(val => !val)) {
        return true
      }
      return false
    },
    getAddressInString (addr) {
      let address = ''
      if (addr.address_1) {
        address += addr.address_1
      } if (addr.address_2) {
        address += `, ${addr.address_2}`
      } if (addr.postcode) {
        address += `, ${addr.postcode}`
      } if (addr.city) {
        address += `, ${addr.city}`
      } if (addr.state) {
        address += `, ${addr.state}`
      } if (addr.country) {
        address += `, ${addr.country}`
      }
      return address
    },
    processMessage() {
      let message = this.messageTemplate
      const selected = this.appointment
      // Process message
      message = message.split('{{name}}').join(selected.patient.name ? encodeURIComponent(selected.patient.name) : '')
      message = message.split('{{doctor}}').join(selected.provider.name ? encodeURIComponent(selected.provider.name) : '')
      message = message.split('{{service}}').join(selected.service.name ? encodeURIComponent(selected.service.name) : '')
      message = message.split('{{date}}').join(selected.date ? encodeURIComponent(this.$options.filters.humanizeDate(selected.date)) : '')
      message = message.split('{{time}}').join(selected.time ? encodeURIComponent(this.$options.filters.displayTime2(selected.time)) : '')
      message = message.split('{{appointment_link}}').join(this.appointmentLink)
      message = message.split('{{review_link}}').join(`${this.reviewLink}/${this.company.slug}?pid=${selected.lead.pid}`)
      message = message.split(/\n/g).join('%0d%0a')
      message = this.$options.filters.encodeSymbols(message)

      return message
    },
    clickWhatsapp() {
      this.isSending = true
      const encodedMessage = this.os === 'clinic' && this.appointment ? this.processMessage() : ''
      const phone = this.os === 'clinic' && this.appointment ? this.appointment.patient.phone : this.selectedCustomer.phone
      window.open(`https://api.whatsapp.com/send?phone=${phone}&text=${encodedMessage}`)
    },
    async fetchAppointment() {
      return this.$axios.get(`/clinics/${this.company.slug}/appointments/${this.selectedAppointment.id}`)
        .then(resp => {
          this.appointment = resp.data
        })
    },
    async fetchReviewSetting() {
      return this.$axios.get(`/reviews/${this.company.slug}`)
        .then(({ data }) => {
          this.reviewSetting = data
          this.$store.commit('review/setSettings', data)
        })
    },
  }
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-basic500);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>